/**
 * Utility functions for date formatting
 * All dates should be displayed in DD/MM/YYYY format (Brazilian standard)
 */

import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Parse a date string safely
 * Handles both ISO format (YYYY-MM-DD) and local date strings
 */
export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  
  // If already a Date object
  if (dateStr instanceof Date) {
    return isValid(dateStr) ? dateStr : null;
  }
  
  // Try parsing as ISO date
  try {
    // Handle date-only format (YYYY-MM-DD) - add time to avoid timezone issues
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    const parsed = parseISO(dateStr);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Format date to DD/MM/YYYY
 */
export const formatDateBR = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return "-";
  return format(date, "dd/MM/yyyy");
};

/**
 * Format date to DD/MM
 */
export const formatDateShort = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return "-";
  return format(date, "dd/MM");
};

/**
 * Format date to "DD de MMMM de YYYY"
 */
export const formatDateLong = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return "-";
  return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
};

/**
 * Format date to "DD MMM" (e.g., "15 Jan")
 */
export const formatDateMonthShort = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return "-";
  return format(date, "dd MMM", { locale: ptBR });
};

/**
 * Format datetime to DD/MM/YYYY HH:mm
 */
export const formatDateTimeBR = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return "-";
  return format(date, "dd/MM/yyyy HH:mm");
};

/**
 * Format date for display in lists (DD/MM/YYYY)
 */
export const formatDateDisplay = (dateStr) => {
  return formatDateBR(dateStr);
};

/**
 * Get day of month
 */
export const getDayOfMonth = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return "--";
  return format(date, "dd");
};

/**
 * Get month abbreviation
 */
export const getMonthAbbr = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return "---";
  return format(date, "MMM", { locale: ptBR }).toUpperCase();
};

/**
 * Format date range (e.g., "01/04/2026 até 15/04/2026")
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDateBR(startDate);
  const end = formatDateBR(endDate);
  return `${start} até ${end}`;
};

export default {
  parseDate,
  formatDateBR,
  formatDateShort,
  formatDateLong,
  formatDateMonthShort,
  formatDateTimeBR,
  formatDateDisplay,
  getDayOfMonth,
  getMonthAbbr,
  formatDateRange
};
